import { Component, HostBinding, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import { WorkflowService } from '../../services/workflow.service';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import * as Reducers from 'src/app/store/reducers';
import { IParticipant, IParticipantConfig} from '@interfaces/interfaces';
import { participantPages } from '@app/pages/participant/participants.routes';
import { InterpolationService, TextInterpolation } from '@app/services/interpolation/interpolation.service';
import { ProjectService } from '@app/services/project/project.service';
import { WorkflowStatus } from '@api/projects/interfaces';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { filter, map } from 'rxjs/operators';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { Router } from '@angular/router';
import { PermissionService } from '@app/services/permission/permission.service';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import _ from 'lodash';
import { isNotNil } from 'ng-zorro-antd/core/util';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';


/**
 * Interpolationstemplates für ParticipantInfoDetails
 */
export type ParticipantInfoDetailsTemplate = {
	showIf: string;
	template: TextInterpolation;
	label: string | undefined;
};

export type ParticipantInfoDetails = {
	label: string | undefined;
	value: string;
};

@Component({
	selector: 'workflow-projektsidebar',
	templateUrl: './projektsidebar.component.html',
	styleUrls: ['./projektsidebar.component.scss'],
	providers: [WorkflowItemNavigationService],
})
export class ProjektSidebarComponent implements OnInit, OnChanges, OnDestroy {
	
	@HostBinding('class.print-hide')
	public readonly printHide = true;
	
	public readonly showQueryEdit$: Observable<boolean>;
	
	@Input() currentParticipant: IParticipant;
	@Input() showEditButton = false;
	
	menuColor: string;
	participantId: string;
	isMobileView: boolean;
	participantInfo: string = null;
	participantRole = '';
	participantCreationDate: string;
	
	goToEditButton: SplitButtonElement[] = [{
		title: 'actions.edit',
		type: 'primary',
		icon: 'edit',
		callback: (): void => this.goToEditPage(),
	}];

	public participantInfoDetails: ParticipantInfoDetails[] = [];
	public showCreationDate = true;
	public showParticipantRole = true;

	private participantEditURL: string = null;
	private subscription = new Subscription();
	private participantInfoTemplate: TextInterpolation;
	private participantInfoDetailsTemplates: ParticipantInfoDetailsTemplate[] = [];
	private workflowStatus: WorkflowStatus;

	private canEdit$: Observable<boolean>;
	private canDelete$: Observable<boolean>;
	public canEditOrDelete$: Observable<boolean>;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	constructor(private store: Store<any>,
				private interpolationService: InterpolationService,
				private projectService: ProjectService,
				private workflowService: WorkflowService,
				private workflowItemNavigationService: WorkflowItemNavigationService,
				private activeParticipant: ActiveParticipantService,
				private router: Router,
				private permissionService: PermissionService,
				@Optional() workflowItemService: WorkflowItemDirective | null
	) {
		this.canEdit$ = this.permissionService.hasProjectPermission$('participants:write');
		this.canDelete$ = this.permissionService.hasProjectPermission$('participants:delete');
		this.canEditOrDelete$ = combineLatest([this.canEdit$, this.canDelete$]).pipe(map(([canEdit, canDelete]) => canEdit || canDelete));
		
		if (workflowItemService) {
			this.showQueryEdit$ = workflowItemService.workflowItem$.pipe(
				map(item => item?.hasQueries ?? false)
			);
		} else {
			this.showQueryEdit$ = of(false);
		}
	}

	ngOnInit(): void {
		this.subscription.add(this.canEditOrDelete$.subscribe(canEditOrDelete => this.goToEditButton[0].disabled = !canEditOrDelete));
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			this.menuColor = state.menuColor;
			this.isMobileView = state.isMobileView;
		}));
		this.subscription.add(
			this.projectService.participantsConfig$.subscribe(config => {
				this.loadParticipantDetailInfos(config);
			})
		);
		this.subscription.add(
			combineLatest([
				this.activeParticipant.participant$.pipe(filter(isNotNil)),
				this.workflowService.workflowStatus$
			]).subscribe(([participant, status])=>{
				this.participantId = participant.id;
				this.workflowStatus = status;
			})
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.currentParticipant != null) {
			this.setParticipant();
		}
	}

	private loadParticipantDetailInfos(config: IParticipantConfig) {
		this.participantInfoTemplate = this.getInterpolation(config?.details?.info?.name);
		this.participantEditURL = participantPages.edit.base();
		this.showCreationDate = config?.details?.info?.showCreationDate ?? true;
		this.showParticipantRole = !config?.details?.info?.details; // Fallback für alte Konfiguration
		
		// Interpolation erzeugen. Die Anzeigeobjekte Werte werden in updateParticipantDetailsInfos erstellt
		const detailsList = config?.details?.info?.details || [];
		this.participantInfoDetailsTemplates = detailsList.map(infoDetail => ({
			template: this.getInterpolation(infoDetail.template),
			showIf: infoDetail.showIf,
			label: infoDetail.label,
		}));
		this.updateParticipantDetailsInfos();
	}

	/**
	 * ParticipantDetails aus dem Datenobjekt aktualisieren.
	 * 
	 * Die Methode wird nach der Initalisierung und danach jedes mal aufgerufen, wenn sich der Teilnehmer ändert
	 */
	private updateParticipantDetailsInfos() {
		this.participantInfo = this.participantInfoTemplate?.interpolate(this.currentParticipant.data) || this.currentParticipant.id;

		// Werte der ParticipantInfoDetails interpolieren und Elemente anhand von showIf ausblenden
		this.participantInfoDetails = this.participantInfoDetailsTemplates
			.filter(infoDetail => !infoDetail.showIf || _.get(this.currentParticipant.data, infoDetail.showIf))
			.map(infoDetail => ({
				label: infoDetail.label,
				value: infoDetail.template?.interpolate(this.currentParticipant.data),
			}));
	}

	private getInterpolation(template: string): TextInterpolation | undefined {
		return template && this.interpolationService.interpolation(template);
	}

	private setParticipant(): void {
		this.participantCreationDate = new Date(this.currentParticipant.creationTimestamp).toLocaleDateString(undefined, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});

		this.participantRole = 'person.participant';
		this.updateParticipantDetailsInfos();
	}

	goToEditPage(): void {
		void this.router.navigate([this.participantEditURL]);
	}
}
